import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Share2, Copy, Mail, MessageSquare, Send, CheckCircle2, Loader2 } from "lucide-react";

const SOCIALS = [
  ["Facebook", "#1877F2", (l, t) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(l)}`],
  ["X", "#000000", (l, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(l)}&text=${encodeURIComponent(t)}`],
  ["WhatsApp", "#25D366", (l, t) => `https://wa.me/?text=${encodeURIComponent(t + " " + l)}`],
  ["LinkedIn", "#0A66C2", (l, t) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(l)}`],
];

export const ShareListingDialog = ({ property }) => {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const link = `${window.location.origin}/property/${property.id}?apply=1`;
  const shareText = `Check out ${property.title} on LocalLease — view details & apply online.`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link manually"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: property.title, text: shareText, url: link }); } catch { /* cancelled */ }
    } else { copyLink(); }
  };

  const sendInvite = async () => {
    if (channel === "email" && !email.trim()) { toast.error("Enter the renter's email"); return; }
    if (channel === "sms" && !phone.trim()) { toast.error("Enter the renter's phone number"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/landlord/properties/${property.id}/invite`, {
        channel, email: email.trim(), phone: phone.trim(), message: message.trim(), origin_url: window.location.origin,
      });
      if (res.data.emailed || res.data.texted) toast.success("Invite sent!");
      else toast.success("Invite prepared — share the link below (email/SMS delivery needs to be configured).");
      setEmail(""); setPhone(""); setMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the invite"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`share-prop-${property.id}`} className="mt-2 w-full border border-forest text-forest rounded-lg py-1.5 text-sm hover:bg-forest/5 flex items-center justify-center gap-1.5">
          <Share2 className="h-3.5 w-3.5" /> Share / Invite to apply
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-2xl max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Invite renters to {property.title}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {/* Direct invite */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-forest mb-2">Invite a specific renter</p>
            <div className="flex gap-2 mb-2" data-testid="share-channel">
              {[["email", "Email", Mail], ["sms", "Text", MessageSquare]].map(([v, label, Icon]) => (
                <button key={v} type="button" onClick={() => setChannel(v)}
                  className={`inline-flex items-center gap-1.5 text-sm rounded-lg px-3 py-1.5 border ${channel === v ? "bg-forest text-white border-forest" : "border-line text-ink hover:bg-bone"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            {channel === "email"
              ? <Input data-testid="share-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="renter@example.com" className="border-line" />
              : <Input data-testid="share-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 503 555 0100" className="border-line" />}
            <Input data-testid="share-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a short personal note (optional)" className="border-line mt-2" />
            <button data-testid="share-send" onClick={sendInvite} disabled={busy} className="mt-2 inline-flex items-center gap-2 bg-forest text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invite
            </button>
          </div>

          {/* Shareable link */}
          <div className="border-t border-line pt-4">
            <Label>Shareable link</Label>
            <div className="mt-1 flex gap-2">
              <input data-testid="share-link" readOnly value={link} onFocus={(e) => e.target.select()} className="flex-1 border border-line rounded-lg px-3 py-2 text-sm bg-bone text-ink" />
              <button data-testid="share-copy" onClick={copyLink} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 rounded-lg text-sm font-medium hover:bg-forest-hover">
                {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {/* Social */}
          <div className="border-t border-line pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-forest mb-2">Share on social</p>
            <div className="flex flex-wrap gap-2">
              {SOCIALS.map(([label, color, build]) => (
                <a key={label} data-testid={`share-${label.toLowerCase()}`} href={build(link, shareText)} target="_blank" rel="noreferrer"
                  className="text-white text-sm rounded-lg px-3 py-1.5 font-medium hover:opacity-90" style={{ backgroundColor: color }}>{label}</a>
              ))}
              {typeof navigator !== "undefined" && navigator.share && (
                <button data-testid="share-native" onClick={nativeShare} className="text-sm rounded-lg px-3 py-1.5 border border-line text-ink hover:bg-bone inline-flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5" /> More…</button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
